import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/lib/adminApi";
import { PageHead, StatusLabel, money } from "@/studio/ui";
import { ClientFields, validateClient } from "@/studio/AddClientDialog";
import { ConsultationHistory } from "@/studio/ProjectPanels";

const ContactCard = ({ client, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});

  const start = () => { setForm({ full_name: client.full_name, phone: client.phone, email: client.email, address: client.address || "" }); setErrors({}); setEditing(true); };
  const save = async (e) => {
    e.preventDefault();
    const errs = validateClient(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    try {
      const { data } = await adminApi.patch(`/studio/clients/${client.id}`, form);
      onSaved(data);
      setEditing(false);
      toast.success("Contact details saved");
    } catch (err) {
      setErrors({ form: formatApiError(err.response?.data?.detail) });
    }
  };

  return (
    <section className="st-panel" data-testid="client-contact-card">
      <div className="st-panel__head">
        <h2 className="st-h2">Contact</h2>
        {!editing && <button className="st-btn st-btn--quiet st-btn--sm" onClick={start} data-testid="client-edit-button"><Pencil size={13} /> Edit</button>}
      </div>
      {editing ? (
        <form onSubmit={save} noValidate style={{ display: "grid", gap: 18 }}>
          <ClientFields form={form} setForm={setForm} errors={errors} prefix="edit-client" />
          {errors.form && <p className="st-error" data-testid="edit-client-form-error">{errors.form}</p>}
          <div style={{ display: "flex", gap: 10 }}>
            <button className="st-btn st-btn--sm" type="submit" data-testid="edit-client-save-button">Save</button>
            <button className="st-btn st-btn--ghost st-btn--sm" type="button" onClick={() => setEditing(false)} data-testid="edit-client-cancel-button">Cancel</button>
          </div>
        </form>
      ) : (
        <dl className="st-dl">
          <dt>Phone</dt><dd data-testid="client-phone">{client.phone}</dd>
          <dt>Email</dt><dd data-testid="client-email">{client.email}</dd>
          <dt>Address</dt><dd data-testid="client-address">{client.address || <span className="st-muted">Not provided</span>}</dd>
        </dl>
      )}
    </section>
  );
};

export default function ClientProfile() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const load = useCallback(() => adminApi.get(`/studio/clients/${id}`).then((r) => setClient(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  if (!client) return <p className="st-loading">Loading…</p>;

  return (
    <div className="st-page" data-testid="client-profile-page">
      <PageHead eyebrow={<Link to="/admin/studio/clients">Clients</Link>} title={client.full_name}>
        <Link to={`/admin/studio/projects/new?client=${client.id}`} className="st-btn" data-testid="client-add-project-button"><Plus size={15} /> Add New Project</Link>
      </PageHead>
      <div className="st-grid2">
        <div>
          <section className="st-panel" data-testid="client-projects">
            <div className="st-panel__head"><h2 className="st-h2">Projects</h2><span className="st-muted st-small">{client.projects.length}</span></div>
            {client.projects.length === 0 ? <p className="st-empty" data-testid="client-no-projects">No projects yet.</p> : (
              <ul className="st-list">
                {client.projects.map((p) => (
                  <li key={p.id}>
                    <Link to={`/admin/studio/projects/${p.id}`} className="st-row st-row--link" data-testid={`client-project-${p.id}`}>
                      <div>
                        <div className="st-row__title">{p.name} {p.is_draft && <span className="st-tag" style={{ marginLeft: 6 }}>Draft</span>}</div>
                        <div className="st-row__meta">{p.rooms_count} rooms · {p.pending ? "Fee pending" : money(p.total)}</div>
                      </div>
                      <StatusLabel status={p.status} />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section className="st-panel">
            <h2 className="st-h2">Consultation history</h2>
            <ConsultationHistory projects={client.projects.filter((p) => p.stages.some((st) => st.status !== "not_started"))} />
          </section>
          <section className="st-panel">
            <h2 className="st-h2">Documents</h2>
            {client.projects.length === 0 ? <p className="st-muted st-small">No projects yet.</p> : (
              <ul className="st-list" data-testid="client-documents">
                {client.projects.map((p) => <li key={p.id} className="st-row"><span>{p.name}<br /><span className="st-muted st-small">{p.documents_count} document{p.documents_count === 1 ? "" : "s"}: proposals, agreements, boards, receipts, files</span></span>
                  <Link to={`/admin/studio/projects/${p.id}?tab=documents`} className="st-btn st-btn--ghost st-btn--sm" data-testid={`client-docs-${p.id}`}>View</Link></li>)}
              </ul>
            )}
          </section>
        </div>
        <div>
          <ContactCard client={client} onSaved={setClient} />
          <section className="st-panel" data-testid="client-payment-summary">
            <h2 className="st-h2">Payments</h2>
            {client.projects.length === 0 ? <p className="st-muted st-small">No projects yet.</p> : (
              <ul className="st-list st-price">
                {client.projects.map((p) => <li key={p.id} data-testid={`client-pay-${p.id}`}><span>{p.name}<br /><span className="st-muted st-small">{p.pay.total == null ? "No signed agreement yet" : `Paid ${money(p.pay.paid_total / 100)} of ${money(p.pay.total / 100)}`}</span></span>
                  <span>{p.pay.total == null ? "—" : `${money(p.pay.outstanding_total / 100)} due`}</span></li>)}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
